/**
 * "Anything need me?" — the cards waiting on this seat's Actions list, one at a
 * time, each with what approving it does, who it reaches and the words that go
 * out, and two taps: put it off, or the card's own verb.
 *
 * Built from the cards `list_actions` already read (`readSpokenCards`: the
 * inbox's own visibility rule and order), so it can show nothing the Actions
 * list would not. What / To / words come from `approvalParts`, the same branch
 * that writes the spoken read-back, so a card the voice will not approve is
 * not approvable here either, for the same reason: money and the books,
 * settings and installs, roll-ups, more than three customers at once, and the
 * cards whose approval sends nothing. Those render as a row into Actions.
 *
 * 🚨 Nothing here sends. The orange tap decides the EXISTING card through
 * `decideFromChat`, exactly as its button in Actions does, with every check
 * that button has. "Not now" SNOOZES (it comes back), never dismisses.
 *
 * Pure and directive-free (anti-pattern #18): the server builds it, the panel
 * and the Mucka Bar draw it, the grader summarises it.
 */
import type { ApprovalParts } from '@/lib/actions/spoken-approval';

/** Cards shown in the stack; the rest are counted and handed to Actions. */
export const APPROVAL_STACK_MAX = 5;

export interface ApprovalStackCardBase {
  /** `engine_actions.id` — what a tap decides. */
  actionId: string;
  /** The card's tag ("Invoice", "Reminder"), as Actions shows it. */
  tag: string | null;
  /** The card's own text, HTML stripped. */
  title: string;
  /** A seeded example card, so the owner is not misled into thinking it is real work. */
  isDemo: boolean;
  /** Where Actions opens this card. */
  path: string;
}

export interface ApprovalStackCardApprovable extends ApprovalStackCardBase {
  approvable: true;
  what: string;
  to: string | null;
  words: string | null;
  /** The orange verb, the card's own primary button label. */
  verb: string;
}

export interface ApprovalStackCardElsewhere extends ApprovalStackCardBase {
  /** Refused by `approvalParts`, or not this seat's to decide: a row into Actions. */
  approvable: false;
}

export type ApprovalStackCard = ApprovalStackCardApprovable | ApprovalStackCardElsewhere;

export interface ApprovalStackBlock {
  kind: 'approval-stack';
  cards: ApprovalStackCard[];
  /** Waiting cards beyond the ones shown. Never hidden silently. */
  more: number;
}

/** One card as `readSpokenCards` returns it, reduced to what the stack reads. */
export interface ApprovalStackCandidate {
  id: string;
  tag: string | null;
  body: string;
  actionType: string;
  primaryLabel: string;
  payload: Record<string, unknown>;
  isDemo: boolean;
}

/**
 * The stack, or null when nothing is waiting.
 *
 * `partsFor` is `approvalParts`, passed in so this file stays free of the
 * server-side card read; `canDecide` is the per-card seat check
 * (`canDecideOnAction`) — a card this seat can see but not decide gets a row,
 * never a button that would only come back "Not found".
 */
export function approvalStackBlock<C extends ApprovalStackCandidate>(
  cards: readonly C[],
  partsFor: (card: C) => ApprovalParts,
  canDecide: (card: C) => boolean,
): ApprovalStackBlock | null {
  if (cards.length === 0) return null;
  const shown = cards.slice(0, APPROVAL_STACK_MAX).map((c): ApprovalStackCard => {
    const base: ApprovalStackCardBase = {
      actionId: c.id,
      tag: c.tag?.trim() || null,
      title: c.body.trim() || c.tag?.trim() || 'A card waiting for you',
      isDemo: c.isDemo,
      path: `/actions?open=${encodeURIComponent(c.id)}`,
    };
    if (!canDecide(c)) return { ...base, approvable: false };
    const parts = partsFor(c);
    if (!parts.ok) return { ...base, approvable: false };
    return {
      ...base,
      approvable: true,
      what: parts.what,
      to: parts.to,
      words: parts.words,
      verb: c.primaryLabel.trim() || 'Approve',
    };
  });
  return { kind: 'approval-stack', cards: shown, more: Math.max(0, cards.length - shown.length) };
}

/**
 * One line for the oversight transcript: counts only, nothing a customer could
 * be identified by.
 */
export function summariseApprovalStackBlock(block: ApprovalStackBlock): string {
  const n = block.cards.length;
  const here = block.cards.filter(c => c.approvable).length;
  const elsewhere = n - here;
  const bits = [
    `${n} card${n === 1 ? '' : 's'} one at a time`,
    `${here} approvable here`,
    ...(elsewhere ? [`${elsewhere} to approve in Actions`] : []),
    ...(block.more ? [`${block.more} more not shown`] : []),
  ];
  return `[approval-stack: ${bits.join(', ')}; taps are Not now (snooze) and the card's own verb]`;
}

/** How each card in the stack ended, in this reply. */
export type StackOutcome = 'approved' | 'put-off' | 'left' | 'gone';

/**
 * The closing line once every card has been stepped through.
 * "2 approved, 1 put off" — and each kind of outcome only when it happened.
 */
export function stackSummary(outcomes: readonly StackOutcome[]): string {
  const count = (o: StackOutcome) => outcomes.filter(x => x === o).length;
  const approved = count('approved');
  const putOff = count('put-off');
  const left = count('left');
  const gone = count('gone');
  const bits = [
    `${approved} approved`,
    ...(putOff ? [`${putOff} put off`] : []),
    ...(left ? [`${left} left in Actions`] : []),
    ...(gone ? [`${gone} already dealt with`] : []),
  ];
  return bits.join(', ');
}

/**
 * The quiet line under the summary. Honest about what a snooze does: the card
 * leaves the list and comes back, it does not stay on screen meanwhile.
 */
export function stackSummaryNote(outcomes: readonly StackOutcome[]): string | null {
  const putOff = outcomes.includes('put-off');
  const left = outcomes.includes('left');
  if (putOff && left) return 'Anything you put off comes back on your Actions list. The rest are still there now.';
  if (putOff) return 'Anything you put off comes back on your Actions list, so nothing is lost.';
  if (left) return 'The ones left are still on your Actions list.';
  return null;
}

/**
 * True when a refused tap means the card is no longer waiting — decided in
 * Actions, on another device, or expired since this reply was written — so
 * trying again is pointless and putting it off would wake a decided card.
 * Matched on the decision route's own sentences (`decideCard`: 409 "That card
 * has already been dealt with.", 404 "Not found").
 */
export function isNoLongerWaiting(message: string | null): boolean {
  if (!message) return false;
  return /already been dealt with|^not found\.?$/i.test(message.trim());
}

/**
 * The owner asking what is waiting for them, in their own words.
 *
 * Typed chat does not advertise `list_actions` (it is deferred to keep every
 * turn's schema small), and the pending cards already ride in its prompt, so
 * "anything need me?" is answered from the prompt and the tool that builds the
 * stack is never called. The route recognises the question instead and puts
 * the stack on screen from the same read (`approvalStackForTurn`).
 *
 * Deliberately narrow: a short message that is ABOUT their waiting cards.
 * "Anything need me before I book Dave?" is a question about Dave, and a
 * missed match only means the answer comes in words, as it always did.
 */
const ASKS_WHAT_IS_WAITING = /^\s*(?:(?:right|ok|okay|so|hi|morning|alright)[,!.]?\s+)?(?:(?:is\s+there\s+)?anything|what(?:'s|’s|\s+is|\s+has)?|have\s+i\s+got\s+anything|any(?:thing)?)\s+(?:need(?:s|ing)?\s+(?:me|my\s+attention)|waiting\s+(?:for|on)\s+me|for\s+me\s+to\s+(?:approve|do|look\s+at)|in\s+my\s+actions|to\s+approve|on\s+my\s+(?:actions|list))(?:\s+(?:today|this\s+morning|right\s+now|now|then|at\s+the\s+moment))?\s*[.?!]*\s*$/i;
const ABOUT_MY_ACTIONS = /^\s*(?:(?:show|read|go\s+through|check)\s+(?:me\s+)?)?(?:my\s+actions|my\s+cards|any\s+actions|actions\s+waiting)\s*[.?!]*\s*$/i;

export function wantsActionList(message: string): boolean {
  const m = message.trim();
  if (!m || m.length > 80) return false;
  return ASKS_WHAT_IS_WAITING.test(m) || ABOUT_MY_ACTIONS.test(m);
}
